import {
  MEDIA_UNIT_SORT_KEYS,
  type MediaUnitSortKey,
  type ExcludableFilterField,
} from '@ooh/contracts';
import {
  FILTER_FIELD_IDS,
  type FilterFieldId,
  CATALOG_ONLY_ARRAY_FIELD_IDS,
  RANGE_FIELD_DEFS,
} from './filter-fields';
import { coerceNoPhoto, coerceExclude } from './sidecar-coercion';

/** Parses a URL query numeric range bound (string or number) to a finite number, or undefined. */
function parseRangeNumber(raw: unknown): number | undefined {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === 'string' && raw !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export interface MediaUnitsSearch {
  sortBy?: MediaUnitSortKey;
  sortDir?: 'asc' | 'desc';
  sortBy2?: MediaUnitSortKey;
  sortDir2?: 'asc' | 'desc';
  campaignId?: number;
  importJobId?: number;
  region?: string[];
  county?: string[];
  agglomeration?: string[];
  city?: string[];
  administrativeAreaIds?: string[];
  formatCode?: string[];
  mediaType?: string[];
  supplierId?: string[];
  lighting?: string[];
  supplierNumber?: string[];
  grwId?: string[];
  nearKey?: string;
  nearValue?: string;
  nearRadius?: string;
  nearPoiId?: string;
  // Offer-context param shared with the map route (`mapSearchSchema`); resolved
  // to an editable DRAFT offer summary by `useOfferContext`, or ignored.
  addToOfferId?: string;
  // Catalog-local sidecar params (not part of SHARED_FILTER_FIELD_IDS).
  search?: string;
  noPhoto?: boolean;
  availabilityFrom?: string;
  availabilityTo?: string;
  exclude?: ExcludableFilterField[];
  // ─── New catalog-only filters (CU-86cataqt9) ───
  material?: string[];
  multiformat?: string[];
  tags?: string[];
  address?: string[];
  // ─── Column-parity filters (catalog-column-filter-parity) ───
  notes?: string[];
  roadNumber?: string[];
  area_min?: number;
  area_max?: number;
  width_min?: number;
  width_max?: number;
  height_min?: number;
  height_max?: number;
  audience_min?: number;
  audience_max?: number;
  catalogPrice_min?: number;
  catalogPrice_max?: number;
  rent_min?: number;
  rent_max?: number;
  exposureCost_min?: number;
  exposureCost_max?: number;
  additionalCosts_min?: number;
  additionalCosts_max?: number;
  discountedPrice_min?: number;
  discountedPrice_max?: number;
  lastMinutePrice_min?: number;
  lastMinutePrice_max?: number;
  starterPrice_min?: number;
  starterPrice_max?: number;
  productionCost_min?: number;
  productionCost_max?: number;
  installationCost_min?: number;
  installationCost_max?: number;
  dismantlingCost_min?: number;
  dismantlingCost_max?: number;
  clientProduction_min?: number;
  clientProduction_max?: number;
  clientInstallation_min?: number;
  clientInstallation_max?: number;
  clientDismantling_min?: number;
  clientDismantling_max?: number;
  clientAdditionalFees_min?: number;
  clientAdditionalFees_max?: number;
}

// eslint-disable-next-line complexity, max-lines-per-function
export function validateMediaUnitsSearch(search: Record<string, unknown>): MediaUnitsSearch {
  const result: MediaUnitsSearch = {};
  const sortBy = search['sortBy'];
  if (typeof sortBy === 'string' && (MEDIA_UNIT_SORT_KEYS as readonly string[]).includes(sortBy))
    result.sortBy = sortBy as MediaUnitSortKey;
  const sortDir = search['sortDir'];
  if (sortDir === 'asc' || sortDir === 'desc') result.sortDir = sortDir;
  const sortBy2 = search['sortBy2'];
  if (typeof sortBy2 === 'string' && (MEDIA_UNIT_SORT_KEYS as readonly string[]).includes(sortBy2))
    result.sortBy2 = sortBy2 as MediaUnitSortKey;
  const sortDir2 = search['sortDir2'];
  if (sortDir2 === 'asc' || sortDir2 === 'desc') result.sortDir2 = sortDir2;
  const campaignId = search['campaignId'];
  if (typeof campaignId === 'number' && Number.isFinite(campaignId)) {
    result.campaignId = campaignId;
  } else if (typeof campaignId === 'string') {
    const parsed = parseInt(campaignId, 10);
    if (!isNaN(parsed)) result.campaignId = parsed;
  }
  const importJobId = search['importJobId'];
  if (typeof importJobId === 'number' && Number.isFinite(importJobId)) {
    result.importJobId = importJobId;
  } else if (typeof importJobId === 'string') {
    const parsed = parseInt(importJobId, 10);
    if (!isNaN(parsed)) result.importJobId = parsed;
  }
  for (const key of FILTER_FIELD_IDS) {
    const val = search[key];
    if (Array.isArray(val)) result[key as FilterFieldId] = val.map(String);
    else if (typeof val === 'string') result[key as FilterFieldId] = [val];
  }
  // Near POI string params
  if (typeof search['nearKey'] === 'string' && search['nearKey'])
    result.nearKey = search['nearKey'];
  if (typeof search['nearValue'] === 'string' && search['nearValue'])
    result.nearValue = search['nearValue'];
  if (typeof search['nearRadius'] === 'string' && search['nearRadius'])
    result.nearRadius = search['nearRadius'];
  if (typeof search['nearPoiId'] === 'string' && search['nearPoiId'])
    result.nearPoiId = search['nearPoiId'];
  const addToOfferId = search['addToOfferId'];
  if (typeof addToOfferId === 'string' && addToOfferId) {
    result.addToOfferId = addToOfferId;
  } else if (typeof addToOfferId === 'number' && Number.isFinite(addToOfferId)) {
    // Router search parsing can yield a JSON number for a bare numeric query value
    // (e.g. `?addToOfferId=1`); coerce to the canonical string type instead of
    // silently dropping the offer-add context. See offer-context-search-schema.ts
    // for the same round-trip issue on the /map route.
    result.addToOfferId = String(addToOfferId);
  }
  // Catalog-local sidecar params
  if (typeof search['search'] === 'string' && search['search']) result.search = search['search'];
  if (coerceNoPhoto(search['noPhoto'])) result.noPhoto = true;
  if (typeof search['availabilityFrom'] === 'string' && search['availabilityFrom'])
    result.availabilityFrom = search['availabilityFrom'];
  if (typeof search['availabilityTo'] === 'string' && search['availabilityTo'])
    result.availabilityTo = search['availabilityTo'];
  const validExclude = coerceExclude(search['exclude']);
  if (validExclude.length) result.exclude = validExclude;
  // ─── New catalog-only filters (CU-86cataqt9) ───
  for (const key of CATALOG_ONLY_ARRAY_FIELD_IDS) {
    const val = search[key];
    if (Array.isArray(val)) result[key] = val.map(String);
    else if (typeof val === 'string') result[key] = [val];
  }
  for (const def of RANGE_FIELD_DEFS) {
    const min = parseRangeNumber(search[def.minKey]);
    if (min !== undefined) (result as Record<string, number>)[def.minKey] = min;
    const max = parseRangeNumber(search[def.maxKey]);
    if (max !== undefined) (result as Record<string, number>)[def.maxKey] = max;
  }
  return result;
}
